import _ from 'lodash';
import { useState, useEffect, useMemo, createContext } from "react";
import { useNavigate } from 'react-router-dom';
import { DragDropContext } from "@hello-pangea/dnd";
import { useMsal } from '@azure/msal-react';
import KanbanAPI from '../../api/kanban-board/kanban-api-factory';
import { Columns } from '../kanban/columns';
import { arrayAny } from '../../utils/arrayUtils';
import { generateObjectId } from '../../utils/idUtils';

export const InspectionKanbanContext = createContext();

export const InspectionKanban = ({ propertyDocument, saveProperty, onDataChange }) => {

    const { instance, inProgress, accounts } = useMsal();
    const navigate = useNavigate();

    const [inspectionBoard, setInspectionBoard] = useState(null);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [movedItem, setMovedItem] = useState(null);
    const [render, rerender] = useState(false);
    const [templateData, setTemplateData] = useState(null);

    const kanbanAPI = useMemo(() => new KanbanAPI(instance, inProgress, accounts, false), [instance, inProgress, accounts]);

    const propertyId = propertyDocument.id;
    const companyId = propertyDocument.property.landlordOrOwner.parent.id;

    useEffect(() => {
        const loadInspectionData = async () => {
            try {

                // Load template data directly
                const templates = await kanbanAPI.loadSystemTemplates();
                setTemplateData(templates);

                // Use the same selectedProject as the table section
                const propertyProjects = await kanbanAPI.getProjectsByProperty(companyId, propertyId, 'Inspection');
                // Find the inspection board for this property
                const board = propertyDocument.property?.selectedProject ? 
                                                            propertyProjects.find(p => p.id === propertyDocument.property.selectedProject) : 
                                                            propertyProjects[0];
                
                if(!propertyDocument.property?.selectedProject && !!board){
                    propertyDocument.property.selectedProject = board.id;
                    const property = { ...propertyDocument.property };
                    await saveProperty(property);
                }

                if (board && board.columns) {
                    // Order items in each column
                    const orderedBoard = {
                        ...board,
                        columns: board.columns.map(column => ({
                            ...column,
                            items: _.orderBy(column.items || [], ['order'], ['asc'])
                        }))
                    };

                    setInspectionBoard(orderedBoard);
                } else {
                    setInspectionBoard(null);
                }
            } catch (error) {
                console.error("Error loading inspection data:", error);
                setInspectionBoard(null);
            } finally {
                setLoading(false);
            }
        };

        loadInspectionData();
    }, [kanbanAPI, companyId, propertyId, propertyDocument.property, saveProperty]);

    const handleOnDragStart = ({ source }) => {
        const sourceColumn = inspectionBoard.columns.find(t => t.name === source.droppableId);
        const tile = sourceColumn.items[source.index];
        tile.saving = true;
        setMovedItem(tile);
    };

    const handleDragEnd = async ({ destination, source }) => {
        if (!destination || !movedItem) {
            return;
        }

        if (destination.index === source.index && destination.droppableId === source.droppableId) {
            setMovedItem(current => current.saving = false);
            return;
        }

        setInspectionBoard(current => {
            const sourceColumn = current.columns.find(t => t.name === source.droppableId);
            const destinationColumn = current.columns.find(t => t.name === destination.droppableId);
            const isLastColumn = _.maxBy(current.columns, c => c.displayOrder).columnId === destinationColumn.columnId;

            movedItem.order = destination.index;
            movedItem.destinationColumnId = destinationColumn.columnId;

            // Handle completion status based on column
            if (sourceColumn !== destinationColumn) {
                if (isLastColumn) {
                    movedItem.completed = new Date().toISOString();
                    movedItem.status = "Closed";
                } else {
                    movedItem.completed = null;
                    movedItem.status = destinationColumn.name === "In Progress" ? "In Progress" : "Open";
                }
            }

            if (sourceColumn !== destinationColumn) {
                sourceColumn.items.splice(sourceColumn.items.indexOf(movedItem), 1);
                destinationColumn.items.splice(destination.index, 0, movedItem);
            } else {
                destinationColumn.items.splice(destinationColumn.items.indexOf(movedItem), 1);
                destinationColumn.items.splice(destination.index, 0, movedItem);
            }

            sourceColumn.items.forEach((item, index) => {
                item.order = index;
            });

            destinationColumn.items.forEach((item, index) => {
                item.order = index;
            });

            return current;
        });

        const data = await kanbanAPI.upsertTile(movedItem, "Inspection");
        updateBoard(data);
        setMovedItem(null);
        rerender(!render);
        // Find the updated tile in the response
        const updatedTile = data.columns?.flatMap(c => c.items).find(t => t.id === movedItem.id);
        onDataChange?.(updatedTile);
    };

    const updateBoard = newData => {
        if (newData && arrayAny(Object.keys(newData))) {
            if (!!movedItem) {
                for (const column of newData.columns) {
                    const movingTile = column.items.find(t => t.id === movedItem.id);
                    if (!!movingTile) {
                        movingTile.saving = false;
                        setMovedItem(movingTile);
                        break;
                    }
                }
            }
            setInspectionBoard(newData);
        }
    };

    const localUpsertTile = async tile => {
        const data = await kanbanAPI.upsertTile(tile, "Inspection");
        updateBoard(data);

        // Find the updated tile in the response
        const updatedTile = data.columns?.flatMap(c => c.items).find(t => t.id === tile.id);
        onDataChange?.(updatedTile);
    };

    const localSetData = (updateMethod) => {
        setInspectionBoard(current => {
            if (!current) return current;
            return updateMethod(current);
        });
    };

    const localAddSubTask = async (project, tileId, newTask) => {
        setInspectionBoard(current => {
            const tile = current.columns.flatMap(c => c.items).find(t => t.id === tileId);
            if (tile) {
                newTask.subTaskId = generateObjectId();
                newTask.order = tile.subTasks?.length || 0;
                tile.subTasks = tile.subTasks || [];
                tile.subTasks.push(newTask);
            }
            return current;
        });
        rerender(!render);
    };

    const localDeleteSubTask = async (project, tileId, subTask) => {
        setInspectionBoard(current => {
            const tile = current.columns.flatMap(c => c.items).find(t => t.id === tileId);
            if (tile && tile.subTasks) {
                tile.subTasks = tile.subTasks.filter(st => st.subTaskId !== subTask.subTaskId);
            }
            return current;
        });
        rerender(!render);
    };

    const localUpdateSubTask = async (project, tileId, updatedTask) => {
        setInspectionBoard(current => {
            const tile = current.columns.flatMap(c => c.items).find(t => t.id === tileId);
            if (tile && tile.subTasks) {
                const index = tile.subTasks.findIndex(st => st.subTaskId === updatedTask.subTaskId);
                if (index !== -1) {
                    tile.subTasks[index] = updatedTask;
                }
            }
            return current;
        });
        rerender(!render);
    };

    const createInspectionBoard = async () => {
        try {
            setCreating(true);
            const companyId = propertyDocument.property.landlordOrOwner.parent.id;
            const propertyId = propertyDocument.id;
            const boardName = propertyDocument.property.name;

            const newBoard = await kanbanAPI.addProject(boardName, companyId, propertyId);
            setInspectionBoard(newBoard);
            
            // Update property association
            propertyDocument.property.selectedProject = newBoard.id;
            const property = { ...propertyDocument.property };
            await saveProperty(property);
        } catch (error) {
            console.error("Error creating inspection board:", error);
        } finally {
            setCreating(false);
        }
    };

    const addInspectionTask = async () => {
        try {
            setCreating(true);
            const firstColumn = inspectionBoard.columns[0];
            
            // Find inspection template
            const inspectionTemplate = templateData?.items?.find(t => t.isInspection);
            
            const newTile = {
                name: "New Inspection Task",
                description: "",
                order: firstColumn.items.length,
                boardId: inspectionBoard.id,
                columnId: firstColumn.columnId,
                subTasks: [],
                tags: [],
                templateId: inspectionTemplate?.id || null,
                completedBy: null,
                dueDate: null,
                completed: null,
                priority: "Low",
                taskType: "Inspection"
            };

            const data = await kanbanAPI.upsertTile(newTile, "Inspection");
            updateBoard(data);
            // Find the new tile in the response
            const createdTile = data.columns?.flatMap(c => c.items).find(t => t.name === newTile.name && !t.completed);
            onDataChange?.(createdTile);
        } catch (error) {
            console.error("Error adding inspection task:", error);
        } finally {
            setCreating(false);
        }
    };

    if (loading && !inspectionBoard) {
        return (
            <div className="text-center p-4">
                <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <InspectionKanbanContext.Provider value={{
            upsertTile: localUpsertTile,
            markTileToDelete: () => { },
            setData: localSetData,
            deleteSubTask: localDeleteSubTask,
            addSubTask: localAddSubTask,
            updateSubTask: localUpdateSubTask,
            moveColumn: () => { },
            deleteColumn: () => { },
            onDataChange
        }}>
            <div>
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h4>Inspection Board</h4>
                    <button
                        className="btn btn-primary btn-sm"
                        onClick={inspectionBoard ? addInspectionTask : createInspectionBoard}
                        disabled={creating}
                    >
                        {creating ? 'Creating...' : inspectionBoard ? 'Add Inspection' : 'Create Inspection Board'}
                    </button>
                </div>

                <div className="row">
                    {inspectionBoard && inspectionBoard.columns ? (
                        <DragDropContext onDragEnd={handleDragEnd} onDragStart={handleOnDragStart}>
                            <Columns
                                project={inspectionBoard}
                                stack={false}
                                showTaskDelete={false}
                                showPriorityBadge={true}
                                showViewDetailsButton={true}
                                onViewDetails={(tile) => navigate(`/property/${propertyDocument?.id || 'default'}/inspection/${tile.id}`)}
                            />
                        </DragDropContext>
                    ) : (
                        <div className="col-12">
                            <div className="alert alert-info">
                                No inspection board found for this property.
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </InspectionKanbanContext.Provider>
    );
};